import React, { useState, useEffect, useContext, useRef, memo } from "react";
import { Card } from "react-bootstrap";
import { ChatContext } from 'context/Context';
import ChatContentBody from "../elvis-history/content/ChatContentBody";
import MessageTextArea from "../elvis-history/content/MessageTextArea";
import WindowHeader from "./WindowHeader";

// window.innerWidth/innerHeight include the scrollbar gutter, which Edge/Chromium
// reserve (~15px) and Firefox's overlay scrollbars do not, so the two browsers
// disagree by the gutter width. documentElement.clientWidth/clientHeight are the
// layout viewport with the gutter already removed and agree across browsers.
const getViewport = () => ({
    width: document.documentElement.clientWidth,
    height: document.documentElement.clientHeight,
});

const ChatWindow = () => {
    const { chatWindowOpen, setChatWindowOpen, setHideSidebar, setCurrentThread, currentThread, threads } = useContext(ChatContext);
    const minSize = 700;
    const [size, setSize] = useState({ right: 160, bottom: 40, width: minSize, height: minSize });
    const windowElement = useRef(null);
    // currentThread is a snapshot - the provider replaces the thread object on every
    // stream chunk, so re-resolve the live one to follow the update
    const activeThread = threads.find(x => x?.id === currentThread?.id) ?? currentThread;
    // On open reset active chat
    useEffect(() => { setCurrentThread(threads[0]); setHideSidebar(true) }, [chatWindowOpen]);

    // Responsive logic
    useEffect(() => {
        const handleResize = () => {
            const { width, height } = getViewport();
            if (width < 768) {
                // Mobile: Fullscreen
                setSize({
                    right: 0,
                    bottom: 0,
                    width: width,
                    height: height * .99,
                });
            } else if (width < 1200) {
                // Tablet: Large floating window
                setSize({
                    right: width * .1,
                    bottom: height * .05,
                    width: width * 0.85,
                    height: height * .9,
                });
            } else {
                // Desktop: Default floating window
                setSize(prev => ({
                    ...prev,
                    right: 160,
                    bottom: 40,
                    width: minSize + 100,
                    height: minSize + 25,
                }));
            }
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Close on outside click
    useEffect(() => {
        if (!chatWindowOpen) return;
        const handleOutsideClick = ({ target }) => {
            if (windowElement.current?.contains(target)) return;
            // The toggle button closes the window itself, so ignore it here
            if (target.closest?.('.chat-btn-container')) return;
            setChatWindowOpen(false);
        };
        document.addEventListener('mousedown', handleOutsideClick);
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, [chatWindowOpen]);

    return (
        <div
            ref={windowElement}
            style={{
                display: chatWindowOpen ? 'block' : 'none',
                height: chatWindowOpen ? size.height : 0,
                width: chatWindowOpen ? size.width : 0,
                // fixed, not absolute: an absolute box resolves against the initial
                // containing block, so it is document-relative and scrolls away from
                // the viewport metrics it was positioned from
                position: 'fixed',
                bottom: (chatWindowOpen ? size.bottom : 0),
                right: (chatWindowOpen ? size.right : 0),
                zIndex: 2147483647,
                opacity: chatWindowOpen ? 1 : 0,
                transition: 'all 500ms',
            }}
        >
            <Card className='p-3 w-100 h-100'>
                <Card.Header className="p-0 m-0">
                    <WindowHeader />
                </Card.Header>
                <Card.Body>
                    <div
                        className="border rounded-3 shadow-sm"
                        style={{
                            height: size.height * .8,
                            maxHeight: '600px',
                            position: 'relative',
                            overflow: 'scroll'
                        }}>
                        <ChatContentBody thread={activeThread} />
                    </div>
                    <div className="d-flex justify-content-center">
                        <MessageTextArea />
                    </div>
                </Card.Body>
            </Card>
        </div>
    )
}

export default memo(ChatWindow);